
import { useState, useEffect } from 'react';

interface CurrencyRate {
  currency: string;
  symbol: string;
  rate: number;
  change: number;
  flag: string;
}

interface NewsItem {
  id: string;
  type: 'news' | 'fund' | 'investment';
  title: string;
  value?: string;
  change?: number;
  icon: string;
}

const CurrencyTicker = () => {
  const [rates, setRates] = useState<CurrencyRate[]>([
    { currency: 'USD', symbol: '$', rate: 18.45, change: -0.23, flag: '🇺🇸' },
    { currency: 'GBP', symbol: '£', rate: 22.87, change: 0.15, flag: '🇬🇧' },
    { currency: 'CNY', symbol: '¥', rate: 2.54, change: -0.08, flag: '🇨🇳' },
    { currency: 'EUR', symbol: '€', rate: 19.92, change: 0.31, flag: '🇪🇺' },
  ]);

  const [newsItems, setNewsItems] = useState<NewsItem[]>([
    { id: '1', type: 'investment', title: 'EU-Africa Investment Package reaches €150B', value: '€150B', change: 12.5, icon: 'ri-funds-line' },
    { id: '2', type: 'news', title: 'European Development Fund approves new African infrastructure projects', icon: 'ri-building-line' },
    { id: '3', type: 'fund', title: 'Africa Infrastructure Fund', value: '$2.8B', change: 8.3, icon: 'ri-bank-line' },
    { id: '4', type: 'investment', title: 'German investment in African renewable energy up 45%', value: '€890M', change: 45.2, icon: 'ri-leaf-line' },
    { id: '5', type: 'news', title: 'France announces new partnership for African agricultural development', icon: 'ri-plant-line' },
    { id: '6', type: 'fund', title: 'European Investment Bank Africa Fund', value: '€4.1B', change: 15.7, icon: 'ri-exchange-funds-line' },
    { id: '7', type: 'investment', title: 'UK increases trade investment in East Africa by 28%', value: '£650M', change: 28.1, icon: 'ri-line-chart-line' },
    { id: '8', type: 'news', title: 'EU Green Deal extends climate funding to African nations', icon: 'ri-earth-line' },
  ]);

  // Simulate real-time updates for currencies
  useEffect(() => {
    const currencyInterval = setInterval(() => {
      setRates(prevRates => 
        prevRates.map(rate => ({
          ...rate,
          rate: rate.rate + (Math.random() - 0.5) * 0.1,
          change: (Math.random() - 0.5) * 0.5
        }))
      );
    }, 5000);

    return () => clearInterval(currencyInterval);
  }, []);

  // Update news and investment data every hour
  useEffect(() => {
    const updateNewsData = () => {
      setNewsItems(prevItems => 
        prevItems.map(item => ({
          ...item,
          value: item.value ? generateNewValue(item.value) : undefined,
          change: item.change ? item.change + (Math.random() - 0.5) * 5 : undefined
        }))
      );
    };

    const newsInterval = setInterval(updateNewsData, 3600000); // 1 hour
    
    return () => clearInterval(newsInterval);
  }, []);

  const generateNewValue = (currentValue: string): string => {
    const match = currentValue.match(/([€$£])(\d+\.?\d*)(B|M)?/);
    if (match) {
      const symbol = match[1];
      const amount = parseFloat(match[2]);
      const unit = match[3] || '';
      const variation = (Math.random() - 0.5) * 0.1;
      const newAmount = (amount * (1 + variation)).toFixed(1);
      return `${symbol}${newAmount}${unit}`;
    }
    return currentValue;
  };

  const allItems = [...rates, ...newsItems];

  return (
    <div className="bg-gray-900 text-white py-3 overflow-hidden relative w-full z-40" style={{ marginTop: '80px' }}>
      <div className="flex animate-scroll whitespace-nowrap" style={{ width: '400%' }}>
        {/* First set */}
        <div className="flex items-center space-x-12 px-8 min-w-full">
          {rates.map((rate, index) => (
            <div key={`curr-1-${index}`} className="flex items-center space-x-3 text-sm">
              <span className="text-xl">{rate.flag}</span>
              <span className="font-semibold text-gray-200">ZAR/{rate.currency}</span>
              <span className="font-bold text-white">{rate.symbol}{rate.rate.toFixed(2)}</span>
              <span className={`text-sm font-medium ${rate.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {rate.change >= 0 ? '+' : ''}{rate.change.toFixed(2)}
              </span>
              <i className={`ri-arrow-${rate.change >= 0 ? 'up' : 'down'}-line text-sm ${rate.change >= 0 ? 'text-green-400' : 'text-red-400'}`}></i>
            </div>
          ))}
          {newsItems.map((item, index) => (
            <div key={`news-1-${index}`} className="flex items-center space-x-3 text-sm">
              <i className={`${item.icon} text-blue-400 text-lg`}></i>
              <span className="font-medium text-gray-200">{item.title}</span>
              {item.value && (
                <>
                  <span className="font-bold text-white">{item.value}</span>
                  {item.change && (
                    <span className={`text-sm font-medium ${item.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {item.change >= 0 ? '+' : ''}{item.change.toFixed(1)}%
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        
        {/* Second set */}
        <div className="flex items-center space-x-12 px-8 min-w-full">
          {rates.map((rate, index) => (
            <div key={`curr-2-${index}`} className="flex items-center space-x-3 text-sm">
              <span className="text-xl">{rate.flag}</span>
              <span className="font-semibold text-gray-200">ZAR/{rate.currency}</span>
              <span className="font-bold text-white">{rate.symbol}{rate.rate.toFixed(2)}</span>
              <span className={`text-sm font-medium ${rate.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {rate.change >= 0 ? '+' : ''}{rate.change.toFixed(2)}
              </span>
              <i className={`ri-arrow-${rate.change >= 0 ? 'up' : 'down'}-line text-sm ${rate.change >= 0 ? 'text-green-400' : 'text-red-400'}`}></i>
            </div>
          ))}
          {newsItems.map((item, index) => (
            <div key={`news-2-${index}`} className="flex items-center space-x-3 text-sm">
              <i className={`${item.icon} text-blue-400 text-lg`}></i>
              <span className="font-medium text-gray-200">{item.title}</span>
              {item.value && (
                <>
                  <span className="font-bold text-white">{item.value}</span>
                  {item.change && (
                    <span className={`text-sm font-medium ${item.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {item.change >= 0 ? '+' : ''}{item.change.toFixed(1)}%
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Third set */}
        <div className="flex items-center space-x-12 px-8 min-w-full">
          {rates.map((rate, index) => (
            <div key={`curr-3-${index}`} className="flex items-center space-x-3 text-sm">
              <span className="text-xl">{rate.flag}</span>
              <span className="font-semibold text-gray-200">ZAR/{rate.currency}</span>
              <span className="font-bold text-white">{rate.symbol}{rate.rate.toFixed(2)}</span>
              <span className={`text-sm font-medium ${rate.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {rate.change >= 0 ? '+' : ''}{rate.change.toFixed(2)}
              </span>
              <i className={`ri-arrow-${rate.change >= 0 ? 'up' : 'down'}-line text-sm ${rate.change >= 0 ? 'text-green-400' : 'text-red-400'}`}></i>
            </div>
          ))}
          {newsItems.map((item, index) => (
            <div key={`news-3-${index}`} className="flex items-center space-x-3 text-sm">
              <i className={`${item.icon} text-blue-400 text-lg`}></i>
              <span className="font-medium text-gray-200">{item.title}</span>
              {item.value && (
                <>
                  <span className="font-bold text-white">{item.value}</span>
                  {item.change && (
                    <span className={`text-sm font-medium ${item.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {item.change >= 0 ? '+' : ''}{item.change.toFixed(1)}%
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Fourth set */}
        <div className="flex items-center space-x-12 px-8 min-w-full">
          {rates.map((rate, index) => (
            <div key={`curr-4-${index}`} className="flex items-center space-x-3 text-sm">
              <span className="text-xl">{rate.flag}</span>
              <span className="font-semibold text-gray-200">ZAR/{rate.currency}</span>
              <span className="font-bold text-white">{rate.symbol}{rate.rate.toFixed(2)}</span>
              <span className={`text-sm font-medium ${rate.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {rate.change >= 0 ? '+' : ''}{rate.change.toFixed(2)}
              </span>
              <i className={`ri-arrow-${rate.change >= 0 ? 'up' : 'down'}-line text-sm ${rate.change >= 0 ? 'text-green-400' : 'text-red-400'}`}></i>
            </div>
          ))}
          {newsItems.map((item, index) => (
            <div key={`news-4-${index}`} className="flex items-center space-x-3 text-sm">
              <i className={`${item.icon} text-blue-400 text-lg`}></i>
              <span className="font-medium text-gray-200">{item.title}</span>
              {item.value && (
                <>
                  <span className="font-bold text-white">{item.value}</span>
                  {item.change && (
                    <span className={`text-sm font-medium ${item.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {item.change >= 0 ? '+' : ''}{item.change.toFixed(1)}%
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Live indicator */}
      <div className="absolute right-6 top-1/2 transform -translate-y-1/2 flex items-center space-x-2 bg-gray-800 px-3 py-1 rounded-full">
        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
        <span className="text-xs font-bold text-green-400">LIVE</span>
      </div>
    </div>
  );
};

export default CurrencyTicker;
